import type { CampaignDuration } from "@/types/campaign";
import type { DatePreset } from "@/stores/filterStore";
import {
  getMinSelectableDate,
  getYesterday,
  parseISODate,
  toISODate,
} from "@/layouts/AppLayout/dateRangePresets";

export interface ResolvedDateRange {
  start: string;
  end: string;
}

// Matches the store's own DateRange shape (ISO strings, not Date instances —
// see filterStore.ts) rather than importing it directly, so this file's
// contract doesn't depend on FilterStore$'s exact type.
interface CustomDateRange {
  start: string | null;
  end: string | null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const WEEK_WINDOW_DAYS = 7;
const MONTH_WINDOW_DAYS = 30;

const min = (first: Date, second: Date): Date =>
  first.getTime() <= second.getTime() ? first : second;
const max = (first: Date, second: Date): Date =>
  first.getTime() >= second.getTime() ? first : second;
const clamp = (date: Date, lowerBound: Date, upperBound: Date): Date =>
  max(lowerBound, min(date, upperBound));

interface ResolveDateRangeArgs {
  datePreset: DatePreset;
  dateRange: CustomDateRange;
  campaignDurations: CampaignDuration[];
  now: Date;
}

// FilterStore$'s DatePreset ("day"/"week"/"month"/"custom") doesn't
// match the PRD's Last Week/Last Month/MTD/QTD vocabulary, and nothing today
// turns a preset into a concrete range — this is the page-local workaround,
// not a fix; that reconciliation belongs to whoever owns FilterStore$.
export const resolveDateRange = ({
  datePreset,
  dateRange,
  campaignDurations,
  now,
}: ResolveDateRangeArgs): ResolvedDateRange => {
  if (campaignDurations.length === 0) {
    throw new Error("resolveDateRange requires at least one campaign duration");
  }
  // Multiple selected campaigns resolve to the union of their windows —
  // earliest start to latest end — rather than any one campaign's own dates.
  const campaignStart = campaignDurations
    .map((duration) => parseISODate(duration.start_date))
    .reduce(min);
  const campaignEnd = campaignDurations
    .map((duration) => parseISODate(duration.end_date))
    .reduce(max);
  // Anchored to yesterday, not now: data freshness is T-1 (see the PRD), so
  // today never has data yet — the same cutoff the Custom picker's own
  // maxDate and quick-selects already use (dateRangePresets.ts).
  // Also clamped so an upcoming campaign (yesterday < start) never produces
  // an inverted range rather than a legitimately empty one.
  const effectiveEnd = max(min(getYesterday(now), campaignEnd), campaignStart);

  // The earliest start any range may have: the campaign's own start, but never
  // before the picker's rolling 1-year floor — it applies to every preset, and
  // to a stale store value alike, so none can send a wider request. Campaigns
  // that ended before the floor are never listed (see buildCampaignsQuery), so
  // it can only pass effectiveEnd for a list cached before a UTC day rollover;
  // capping it at effectiveEnd keeps that case a valid single day, not inverted.
  const windowStart = min(
    max(campaignStart, getMinSelectableDate(now)),
    effectiveEnd
  );

  if (datePreset === "custom" && dateRange.start && dateRange.end) {
    // Each bound is clamped into the campaign window independently, then
    // start/end are taken as the min/max of those two clamped bounds —
    // if the picked range falls entirely outside the campaign (e.g.
    // wholly before campaignStart), both bounds clamp to the same edge
    // instead of producing an inverted (start-after-end) range.
    const clampedStart = clamp(
      parseISODate(dateRange.start),
      windowStart,
      effectiveEnd
    );
    const clampedEnd = clamp(
      parseISODate(dateRange.end),
      windowStart,
      effectiveEnd
    );
    return {
      start: toISODate(min(clampedStart, clampedEnd)),
      end: toISODate(max(clampedStart, clampedEnd)),
    };
  }

  if (datePreset === "day") {
    return { start: toISODate(effectiveEnd), end: toISODate(effectiveEnd) };
  }

  if (datePreset === "week") {
    const weekStart = max(
      new Date(effectiveEnd.getTime() - (WEEK_WINDOW_DAYS - 1) * MS_PER_DAY),
      windowStart
    );
    return { start: toISODate(weekStart), end: toISODate(effectiveEnd) };
  }

  if (datePreset === "month") {
    const monthStart = max(
      new Date(effectiveEnd.getTime() - (MONTH_WINDOW_DAYS - 1) * MS_PER_DAY),
      windowStart
    );
    return { start: toISODate(monthStart), end: toISODate(effectiveEnd) };
  }

  // "custom" without both dates set.
  return { start: toISODate(windowStart), end: toISODate(effectiveEnd) };
};
